import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { VibeOption } from '@/types/assessment';

interface VibeChipProps {
  option: VibeOption;
  selected: boolean;
  onToggle: (id: string) => void;
}

const VibeChip = ({ option, selected, onToggle }: VibeChipProps) => {
  const [expanded, setExpanded] = useState(false);
  const expandedId = `vibe-expanded-${option.id}`;

  return (
    <div className="flex flex-col">
      {/* Card row — toggle and expand are sibling buttons, never nested */}
      <motion.div
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        className={`flex items-center rounded-xl border transition-all duration-200 ${
          selected ? 'chip-selected glow-teal' : 'chip-default'
        }`}
      >
        {/* Toggle button — takes up the main label area */}
        <button
          type="button"
          onClick={() => onToggle(option.id)}
          aria-pressed={selected}
          className="flex-1 flex items-center gap-3 px-5 py-4 text-left min-w-0"
        >
          <div className="flex-1 min-w-0">
            <span className="font-semibold text-sm">{option.label}</span>
            <p className="text-xs text-muted-foreground mt-0.5">{option.description}</p>
          </div>
        </button>

        {/* Expand button — separate, never nested inside toggle */}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          aria-controls={expandedId}
          aria-label={`${expanded ? 'Hide' : 'Show'} pros and cons for ${option.label}`}
          className="p-3 rounded-r-xl hover:bg-primary/20 transition-colors shrink-0"
        >
          <ChevronDown
            className={`w-4 h-4 text-muted-foreground transition-transform ${expanded ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
      </motion.div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div id={expandedId} className="mt-2 px-4 py-3 rounded-lg bg-secondary/50 border border-border/50 text-sm grid grid-cols-2 gap-4">
              <div>
                <p className="text-primary font-medium mb-1 text-xs uppercase tracking-wider">Pros</p>
                <ul className="space-y-1">
                  {option.pros.map((pro) => (
                    <li key={pro} className="text-foreground/80 flex items-center gap-1.5">
                      <span className="text-primary text-xs" aria-hidden="true">+</span> {pro}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-accent font-medium mb-1 text-xs uppercase tracking-wider">Cons</p>
                <ul className="space-y-1">
                  {option.cons.map((con) => (
                    <li key={con} className="text-foreground/80 flex items-center gap-1.5">
                      <span className="text-accent text-xs" aria-hidden="true">–</span> {con}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VibeChip;
